import { useReportClock, reportDate } from '../../reportClock';
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumbs } from "../../../../AbstractElements";
import { Container } from "reactstrap";
import { AlertCircle, Calendar, Clock, Layers, MapPin, Pause, Play, RefreshCw } from "react-feather";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./trackingReplay.scss";
import {
  detectRouteStops,
  formatReplayTime,
  getRelativeDateValue,
  groupTicketActivityBySite,
  getVisibleReplayData,
  prefersLightweightMap,
  sampleRoutePoints,
} from "./trackingReplayUtils";
import { formatTorontoEventTime } from "../../../../utils/eventTruth";
import { loadTrackingReplayData } from "./trackingLiveData";

const STATUS_LABELS = {
  active: "En cours",
  paused: "En pause",
  disabled: "Suivi désactivé",
  permission_disabled: "Permission désactivée",
  offline: "Hors ligne",
  no_data: "Aucune donnée",
};

const SPEEDS = [0.5, 1, 2, 4];
const END_OF_DAY_MINUTE = 1439;
const NORMAL_ROUTE_POINT_LIMIT = 800;
const LIGHTWEIGHT_ROUTE_POINT_LIMIT = 250;

const getCurrentMinute = () => {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'America/Toronto', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
  return Number(parts.find(p => p.type === 'hour').value) * 60 + Number(parts.find(p => p.type === 'minute').value);
};

const getInitials = (name) => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part.charAt(0))
  .join("")
  .toUpperCase();

const escapeHtml = value => String(value || "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const AgentList = ({ agents, selectedAgentId, onSelectAgent }) => (
  <aside className="tracking-agent-panel" aria-label="Liste des agents">
    <div className="tracking-agent-panel__header">
      <strong>Agents ({agents.length})</strong>
      <span>PostgreSQL en direct</span>
    </div>
    <div className="tracking-agent-panel__list">
      {agents.map((agent) => (
        <button
          type="button"
          key={agent.id}
          className={`tracking-agent-row${selectedAgentId === agent.id ? " is-selected" : ""}`}
          onClick={() => onSelectAgent(selectedAgentId === agent.id ? "all" : agent.id)}
        >
          <span className="tracking-agent-row__color" style={{ backgroundColor: agent.color }} />
          <span
            className="tracking-agent-row__avatar"
            style={{ borderColor: agent.color }}
            aria-hidden="true"
          >
            {getInitials(agent.name)}
          </span>
          <span className="tracking-agent-row__identity">
            <strong>{agent.name}</strong>
            <small>{agent.role === "admin" ? "Administrateur" : "Agent"}</small>
          </span>
          <span className={`tracking-agent-row__status status-${agent.status}`}>
            {STATUS_LABELS[agent.status]}
          </span>
        </button>
      ))}
      {!agents.length ? (
        <div className="tracking-agent-panel__empty">Aucun agent pour ces filtres.</div>
      ) : null}
    </div>
  </aside>
);

const ReplayControls = ({
  currentMinute,
  isPlaying,
  maxMinute,
  onGoToNow,
  onPlayToggle,
  onMinuteChange,
  speed,
  onSpeedChange,
}) => (
  <section className="tracking-playback" aria-label="Commandes de relecture">
    <div className="tracking-playback__primary-controls">
      <button
        type="button"
        className="tracking-playback__play"
        onClick={onPlayToggle}
        aria-label={isPlaying ? "Mettre la relecture en pause" : "Démarrer la relecture"}
      >
        {isPlaying ? <Pause size={22} /> : <Play size={22} />}
      </button>
      {onGoToNow ? (
        <button
          type="button"
          className="tracking-playback__now"
          onClick={onGoToNow}
          aria-label="Revenir à maintenant"
          title="Maintenant"
        >
          <Clock size={20} />
          <span>Maintenant</span>
        </button>
      ) : null}
    </div>
    <strong className="tracking-playback__time">{formatReplayTime(currentMinute)}</strong>
    <div className="tracking-playback__timeline">
      <output style={{ left: `${(currentMinute / Math.max(maxMinute, 1)) * 100}%` }}>
        {formatReplayTime(currentMinute)}
      </output>
      <input
        type="range"
        min="0"
        max={maxMinute}
        step="1"
        value={currentMinute}
        onChange={(event) => onMinuteChange(Number(event.target.value))}
        aria-label="Heure de la relecture"
      />
      <div className="tracking-playback__ticks" aria-hidden="true">
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
          <span key={ratio}>{formatReplayTime(Math.round(maxMinute * ratio))}</span>
        ))}
      </div>
    </div>
    <span className="tracking-playback__end">{formatReplayTime(maxMinute)}</span>
    <div className="tracking-playback__speeds" aria-label="Vitesse de lecture">
      {SPEEDS.map((value) => (
        <button
          type="button"
          key={value}
          className={speed === value ? "is-active" : ""}
          onClick={() => onSpeedChange(value)}
        >
          {value}x
        </button>
      ))}
    </div>
  </section>
);

const TrackingReplay = () => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const reportNow = useReportClock();
  const today = reportDate(reportNow);
  const [selectedDate, setSelectedDate] = useState(today);
  const [datePreset, setDatePreset] = useState("today");
  const [role, setRole] = useState("all");
  const [agentId, setAgentId] = useState("all");
  const [status, setStatus] = useState("all");
  const [zone, setZone] = useState("all");
  const [layerMode, setLayerMode] = useState("all");
  const [lightweightMap, setLightweightMap] = useState(() => prefersLightweightMap({
    userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent,
    deviceMemory: typeof navigator === "undefined" ? undefined : navigator.deviceMemory,
    hardwareConcurrency: typeof navigator === "undefined" ? undefined : navigator.hardwareConcurrency,
    search: typeof window === "undefined" ? "" : window.location.search,
  }));
  const [nowMinute, setNowMinute] = useState(getCurrentMinute);
  const [currentMinute, setCurrentMinute] = useState(getCurrentMinute);
  const [isPlaying, setIsPlaying] = useState(false);
  const [followNow, setFollowNow] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [dataset, setDataset] = useState({
    synthetic: false,
    date: today,
    timezone: "America/Toronto",
    agents: [],
    positions: [],
    events: [],
    ticketActivityEvents: [],
  });
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (datePreset === 'today') setSelectedDate(today);
    if (datePreset === 'yesterday') setSelectedDate(getRelativeDateValue(-1));
  }, [datePreset, today]);
  const maxMinute = selectedDate === today ? nowMinute : END_OF_DAY_MINUTE;
  const visibleData = useMemo(
    () => getVisibleReplayData(dataset, {
      currentMinute,
      role,
      agentId,
      status,
      zone,
    }),
    [agentId, currentMinute, dataset, role, status, zone]
  );
  const ticketSites = useMemo(
    () => groupTicketActivityBySite(visibleData.ticketActivityEvents, { currentMinute }),
    [currentMinute, visibleData.ticketActivityEvents]
  );
  const agentsWithGps = useMemo(() => {
    const agentIds = new Set(dataset.positions.map((position) => position.agentId));
    return dataset.agents.filter((agent) => agentIds.has(agent.id));
  }, [dataset.agents, dataset.positions]);
  const zones = useMemo(
    () => [...new Set(agentsWithGps.map((agent) => agent.zone).filter(Boolean))].sort(),
    [agentsWithGps]
  );

  useEffect(() => {
    const updateNow = () => {
      const minute = getCurrentMinute();
      setNowMinute(minute);
      if (followNow && datePreset === 'today') setCurrentMinute(minute);
    };
    updateNow();
    const timer = window.setInterval(updateNow, 30000);
    return () => window.clearInterval(timer);
  }, [followNow, datePreset, reportNow]);

  useEffect(() => {
    setCurrentMinute((minute) => Math.min(minute, maxMinute));
  }, [maxMinute]);

  useEffect(() => {
    let active = true;
    let refreshTimer;

    const load = async (showLoading = true) => {
      if (showLoading) setLoadState("loading");
      setLoadError("");
      try {
        const nextDataset = await loadTrackingReplayData({ date: selectedDate });
        if (!active) return;
        setDataset(nextDataset);
        setLoadState(nextDataset.agents.length ? "ready" : "empty");
      } catch (error) {
        if (!active) return;
        setLoadError(error.message);
        setLoadState("error");
      }
    };

    load();
    if (selectedDate === today) {
      refreshTimer = window.setInterval(() => load(false), 30000);
    }

    return () => {
      active = false;
      if (refreshTimer) window.clearInterval(refreshTimer);
    };
  }, [reloadKey, selectedDate, today]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return undefined;

    const map = L.map(mapContainerRef.current, {
      center: [45.53, -73.57],
      zoom: 10,
      zoomControl: true,
      preferCanvas: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
      updateWhenIdle: true,
      keepBuffer: 1,
    }).addTo(map);
    const overlays = L.layerGroup().addTo(map);
    mapRef.current = map;
    overlayRef.current = overlays;

    window.setTimeout(() => map.invalidateSize(), 0);

    return () => {
      map.remove();
      mapRef.current = null;
      overlayRef.current = null;
    };
  }, []);

  useEffect(() => {
    const overlays = overlayRef.current;
    if (!overlays) return;

    overlays.clearLayers();
    const agentMap = new Map(dataset.agents.map((agent) => [agent.id, agent]));
    const groupedPoints = new Map();
    const positionLimit = lightweightMap
      ? LIGHTWEIGHT_ROUTE_POINT_LIMIT
      : NORMAL_ROUTE_POINT_LIMIT;
    const positionsByAgent = new Map();
    visibleData.positions.forEach((point) => {
      const points = positionsByAgent.get(point.agentId) || [];
      points.push(point);
      positionsByAgent.set(point.agentId, points);
    });
    const routePoints = [
      ...[...positionsByAgent.values()].flatMap(points => sampleRoutePoints(points, positionLimit))
        .map(point => ({ ...point, routeSource: "gps" })),
      ...visibleData.ticketActivityEvents.map(event => ({
        ...event,
        routeSource: "ticket",
      })),
    ].sort((left, right) => (
      new Date(left.occurredAt).getTime() - new Date(right.occurredAt).getTime()
    ));
    routePoints.forEach((point) => {
      const points = groupedPoints.get(point.agentId) || [];
      points.push(point);
      groupedPoints.set(point.agentId, points);
    });

    if (layerMode !== "events") {
      groupedPoints.forEach((points, visibleAgentId) => {
        const agent = agentMap.get(visibleAgentId);
        if (!agent || !points.length) return;

        L.polyline(
          points.map((point) => [point.latitude, point.longitude]),
          { color: agent.color, weight: 4, opacity: 0.9 }
        ).addTo(overlays);

        points.filter(point => point.routeSource === "ticket").forEach((point) => {
          L.circleMarker([point.latitude, point.longitude], {
            radius: 7,
            color: agent.color,
            fillColor: "#fbbf24",
            fillOpacity: 1,
            weight: 3,
          }).bindTooltip(
            `${agent.name} · Avis créé · ${formatReplayTime(point.minute)}`
          ).addTo(overlays);
        });

        detectRouteStops(points.filter(point => point.routeSource === "gps")).forEach((stop) => {
          L.circleMarker([stop.latitude, stop.longitude], {
            radius: 6,
            color: "#f8fafc",
            fillColor: "#111827",
            fillOpacity: 1,
            weight: 3,
          }).bindTooltip(
            `${agent.name} · Arrêt de ${stop.durationMinutes} min · `
            + `${formatReplayTime(stop.startMinute)}–${formatReplayTime(stop.endMinute)}`
          ).addTo(overlays);
        });

        const firstPoint = points[0];
        const latestPoint = points[points.length - 1];
        const endpointMarker = (point, label, className, tooltip) => {
          const icon = L.divIcon({
            className: `tracking-route-endpoint ${className}`,
            html: `<span style="--agent-color:${escapeHtml(agent.color)}">${label}</span>`,
            iconSize: [28, 28],
            iconAnchor: [14, 14],
          });
          L.marker([point.latitude, point.longitude], { icon })
            .bindTooltip(`${agent.name} · ${tooltip} · ${formatReplayTime(point.minute)}`)
            .addTo(overlays);
        };

        endpointMarker(
          firstPoint,
          "D",
          "tracking-route-endpoint--start",
          "Départ"
        );
        endpointMarker(
          latestPoint,
          "F",
          "tracking-route-endpoint--end",
          "Dernière géolocalisation"
        );
      });
    }

    if (layerMode !== "routes") {
      visibleData.events.forEach((event) => {
        const agent = agentMap.get(event.agentId);
        const points = groupedPoints.get(event.agentId) || [];
        const nearestPoint = points.reduce((nearest, point) => (
          Math.abs(point.minute - event.minute) < Math.abs((nearest?.minute ?? 0) - event.minute)
            ? point
            : nearest
        ), null);
        if (!agent || !nearestPoint) return;

        const icon = L.divIcon({
          className: "tracking-event-marker",
          html: "<span>!</span>",
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        });
        L.marker([nearestPoint.latitude, nearestPoint.longitude], { icon })
          .bindTooltip(`${agent.name} · ${event.label}`)
          .addTo(overlays);
      });
    }

    ticketSites.forEach((site) => {
      const totalCount = site.createdCount + site.printedCount;
      const markerSize = Math.min(46, Math.round(16 + (Math.sqrt(totalCount) * 6)));
      const siteAgentNames = [...new Set([
        ...site.agentNames,
        ...site.agentIds.map((id) => agentMap.get(id)?.name),
      ].filter(Boolean))];
      const agentLabel = siteAgentNames.length > 1 ? "Agents" : "Agent";
      const agentNames = siteAgentNames.length
        ? siteAgentNames.map(escapeHtml).join(", ")
        : "Non identifié";
      const latestCreatedTime = formatTorontoEventTime(Date.parse(site.latestCreatedAt));
      const latestPrintedTime = formatTorontoEventTime(Date.parse(site.latestPrintedAt));
      const icon = L.divIcon({
        className: "tracking-ticket-site-marker",
        html: '<span aria-hidden="true"></span>',
        iconSize: [markerSize, markerSize],
        iconAnchor: [markerSize / 2, markerSize / 2],
      });
      L.marker([site.latitude, site.longitude], { icon })
        .bindPopup([
          '<div class="tracking-ticket-site-popup">',
          '<strong>Activité du site</strong>',
          `<span class="tracking-ticket-site-popup__agents"><em>${agentLabel}</em><b>${agentNames}</b></span>`,
          `<span><b>${site.createdCount}</b> avis créés</span>`,
          `<span><b>${site.printedCount}</b> avis imprimés</span>`,
          latestCreatedTime ? `<span><b>${latestCreatedTime}</b> dernier avis créé</span>` : '',
          latestPrintedTime ? `<span><b>${latestPrintedTime}</b> dernier avis imprimé</span>` : '',
          '</div>',
        ].join(""))
        .addTo(overlays);
    });
  }, [dataset.agents, layerMode, lightweightMap, ticketSites, visibleData.events, visibleData.positions]);

  useEffect(() => {
    if (!isPlaying) return undefined;

    const timer = window.setInterval(() => {
      setCurrentMinute((minute) => {
        const nextMinute = minute + (speed * 4);
        if (nextMinute >= maxMinute) {
          setIsPlaying(false);
          return maxMinute;
        }
        return nextMinute;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isPlaying, maxMinute, speed]);

  const setPresetDate = (preset) => {
    setDatePreset(preset);
    setFollowNow(preset === 'today');
    if (preset === "today") setSelectedDate(today);
    if (preset === "yesterday") setSelectedDate(getRelativeDateValue(-1));
    setCurrentMinute(preset === "today" ? getCurrentMinute() : END_OF_DAY_MINUTE);
    setIsPlaying(false);
  };

  return (
    <Fragment>
      <Breadcrumbs
        mainTitle="Parcours des agents"
        parent="Données"
        title="Relecture des déplacements"
      />
      <Container fluid className="tracking-replay">
        <section className="tracking-toolbar" aria-label="Filtres du parcours">
          <div className="tracking-toolbar__presets">
            <button type="button" className={datePreset === "today" ? "is-active" : ""} onClick={() => setPresetDate("today")}>
              Aujourd’hui
            </button>
            <button type="button" className={datePreset === "yesterday" ? "is-active" : ""} onClick={() => setPresetDate("yesterday")}>
              Hier
            </button>
            <button type="button" className={datePreset === "custom" ? "is-active" : ""} onClick={() => setPresetDate("custom")}>
              Date personnalisée
            </button>
          </div>
          <label className="tracking-toolbar__date">
            <Calendar size={16} />
            <input
              type="date"
              value={selectedDate}
              max={today}
              onChange={(event) => {
                const nextDate = event.target.value > today ? today : event.target.value;
                setDatePreset(nextDate === today ? "today" : "custom");
                setSelectedDate(nextDate);
                setCurrentMinute(nextDate === today ? getCurrentMinute() : END_OF_DAY_MINUTE);
                setIsPlaying(false);
              }}
            />
          </label>
          <select value={role} onChange={(event) => setRole(event.target.value)} aria-label="Filtrer par rôle">
            <option value="all">Tous les rôles</option>
            <option value="agent">Agents</option>
            <option value="admin">Administrateurs</option>
          </select>
          <select value={agentId} onChange={(event) => setAgentId(event.target.value)} aria-label="Filtrer par agent">
            <option value="all">Tous les agents</option>
            {agentsWithGps.map((agent) => <option value={agent.id} key={agent.id}>{agent.name}</option>)}
          </select>
          <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filtrer par statut">
            <option value="all">Tous les statuts</option>
            <option value="active">En cours</option>
            <option value="paused">En pause</option>
            <option value="disabled">Suivi désactivé</option>
            <option value="permission_disabled">Permission désactivée</option>
            <option value="offline">Hors ligne</option>
            <option value="no_data">Aucune donnée</option>
          </select>
          <select value={zone} onChange={(event) => setZone(event.target.value)} aria-label="Filtrer par zone">
            <option value="all">Toutes les zones</option>
            {zones.map((zoneValue) => (
              <option value={zoneValue} key={zoneValue}>{zoneValue}</option>
            ))}
          </select>
          <label className="tracking-toolbar__layers">
            <Layers size={16} />
            <select value={layerMode} onChange={(event) => setLayerMode(event.target.value)} aria-label="Sélecteur de couche">
              <option value="all">Toutes les couches</option>
              <option value="routes">Trajets et arrêts</option>
              <option value="events">Événements</option>
            </select>
          </label>
          <button
            type="button"
            className="tracking-toolbar__refresh"
            onClick={() => setReloadKey((value) => value + 1)}
            disabled={loadState === "loading"}
          >
            <RefreshCw size={16} />
            Actualiser
          </button>
          <button
            type="button"
            className={`tracking-toolbar__refresh${lightweightMap ? " is-active" : ""}`}
            onClick={() => setLightweightMap((value) => !value)}
            aria-pressed={lightweightMap}
            title="Réduit le nombre de points dessinés sans modifier les données chargées"
          >
            {lightweightMap ? "Mode allégé actif" : "Mode allégé"}
          </button>
        </section>

        {loadState === "loading" ? (
          <section className="tracking-feedback" role="status">
            <RefreshCw className="tracking-feedback__spinner" size={22} />
            Chargement du suivi des agents…
          </section>
        ) : null}
        {loadState === "error" ? (
          <section className="tracking-feedback is-error" role="alert">
            <AlertCircle size={22} />
            <div>
              <strong>Impossible de charger le suivi en direct.</strong>
              <span>{loadError}</span>
            </div>
          </section>
        ) : null}
        {loadState === "empty" ? (
          <section className="tracking-feedback" role="status">
            <MapPin size={22} />
            Aucune donnée de suivi pour le {selectedDate}.
          </section>
        ) : null}

        <div className="tracking-workspace">
          <div className="tracking-map-shell">
            <div ref={mapContainerRef} className="tracking-map" aria-label="Carte des parcours des agents" />
            <div className="tracking-map-badge">
              <MapPin size={14} />
              {selectedDate === today && followNow ? "Direct" : "Historique"} · America/Toronto
              {lightweightMap ? " · Rendu allégé" : ""}
            </div>
          </div>
          <AgentList agents={visibleData.agents} selectedAgentId={agentId} onSelectAgent={setAgentId} />
        </div>

        <div className="tracking-legend" aria-label="Légende de la carte">
          <span><i className="legend-route" />Trajet</span>
          <span><i className="legend-endpoint legend-endpoint-start">D</i>Départ</span>
          <span><i className="legend-endpoint legend-endpoint-end">F</i>Fin</span>
          <span><i className="legend-stop" />Arrêt</span>
          <span><i className="legend-event">!</i>Événement</span>
          <span><i className="legend-offline" />Hors ligne</span>
          <span><i className="legend-disabled" />Désactivé</span>
        </div>

        <ReplayControls
          currentMinute={currentMinute}
          isPlaying={isPlaying}
          maxMinute={maxMinute}
          onGoToNow={selectedDate === today ? () => {
            const minute = getCurrentMinute();
            setFollowNow(true);
            setNowMinute(minute);
            setCurrentMinute(minute);
            setIsPlaying(false);
          } : undefined}
          onPlayToggle={() => {
            setFollowNow(false);
            if (currentMinute >= maxMinute) setCurrentMinute(0);
            setIsPlaying((value) => !value);
          }}
          onMinuteChange={(minute) => {
            setFollowNow(false);
            setCurrentMinute(Math.min(minute, maxMinute));
            setIsPlaying(false);
          }}
          speed={speed}
          onSpeedChange={setSpeed}
        />
      </Container>
    </Fragment>
  );
};

export default TrackingReplay;
